import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle, Clock, AlertCircle, User, Mail, Phone, MapPin, CreditCard, Ticket } from 'lucide-react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import { transactionService } from '../services/api'
import toast, { Toaster } from 'react-hot-toast'
import CryptoJS from 'crypto-js'

const ResultadoTransaccion = () => {
  const [searchParams] = useSearchParams()
  const [transaction, setTransaction] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const navigate = useNavigate()

  const transactionId = searchParams.get('id')
  const reference = searchParams.get('ref')

  // Función para generar el token SHA256 para WOMPI
  const generateWompiSignature = (reference, amount) => {
    const integrityKey = 'prod_integrity_XTVHnUzRFoQe5N6VRgQKvgTDCpt1bxCf'
    const amountInCents = amount * 100
    const concatenatedString = `${reference}${amountInCents}COP${integrityKey}`
    return CryptoJS.SHA256(concatenatedString).toString()
  }

  // Función para consultar directamente la API de Wompi
  const fetchWompiTransactionStatus = async (wompiTransactionId) => {
    try {
      const response = await fetch(`https://production.wompi.co/v1/transactions/${wompiTransactionId}`)
      if (!response.ok) {
        throw new Error('Error al consultar Wompi API')
      }
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error consultando Wompi:', error)
      throw error
    }
  }

  const fetchTransactionStatus = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setRefreshing(true)
      }
      
      let transactionData = null
      
      // Si tenemos un ID de transacción de Wompi, consultamos directamente su API
      if (transactionId) {
        try {
          const wompiData = await fetchWompiTransactionStatus(transactionId)
          
          // Si la transacción está aprobada en Wompi, actualizamos nuestro backend
          if (wompiData.data && wompiData.data.status === 'APPROVED') {
            // Buscar la transacción local por referencia
            try {
              const localResponse = await transactionService.getTransactionStatus(wompiData.data.reference)
              transactionData = localResponse.data
              
              // Si la transacción local aún está pendiente, actualizarla
              if (transactionData && transactionData.status === 'pending') {
                await transactionService.updateTransactionStatus(wompiData.data.reference, 'approved')
                // Volver a consultar para obtener los datos actualizados
                const updatedResponse = await transactionService.getTransactionStatus(wompiData.data.reference)
                transactionData = updatedResponse.data
              }
            } catch (localError) {
              console.error('Error consultando transacción local:', localError)
            }
          } else {
            // Si no está aprobada, intentar consultar por referencia si la tenemos
            if (wompiData.data && wompiData.data.reference) {
              try {
                const localResponse = await transactionService.getTransactionStatus(wompiData.data.reference)
                transactionData = localResponse.data
              } catch (localError) {
                console.error('Error consultando transacción local por referencia:', localError)
              }
            }
          }
          
          // Si no encontramos datos locales, crear un objeto básico con los datos de Wompi
          if (!transactionData && wompiData.data) {
            transactionData = {
              reference: wompiData.data.reference,
              status: wompiData.data.status === 'APPROVED' ? 'approved' : 
                     wompiData.data.status === 'DECLINED' ? 'failed' : 'pending',
              totalAmount: wompiData.data.amount_in_cents / 100,
              created_at: wompiData.data.created_at,
              customer_data: {
                name: 'N/A',
                email: 'N/A',
                phone: 'N/A',
                document: 'N/A',
                city: 'N/A'
              },
              sorteo_name: 'N/A',
              quantity: 'N/A',
              tickets: []
            }
          }
        } catch (wompiError) {
          console.error('Error consultando Wompi API:', wompiError)
          // Si falla Wompi, intentar consultar solo localmente
          if (reference) {
            const localResponse = await transactionService.getTransactionStatus(reference)
            transactionData = localResponse.data
          }
        }
      } else if (reference) {
        // Si solo tenemos referencia, consultar localmente
        const response = await transactionService.getTransactionStatus(reference)
        transactionData = response.data
      }
      
      if (transactionData) {
        setTransaction(transactionData)
        setError(null)
      } else {
        setError('No se pudo obtener información de la transacción')
      }
      
    } catch (err) {
      console.error('Error al obtener estado de transacción:', err)
      setError('No se pudo obtener el estado de la transacción')
      toast.error('Error al consultar el estado de la transacción', {
        duration: 4000,
        position: 'top-center'
      })
    } finally {
      if (isManualRefresh) {
        setRefreshing(false)
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    // Permitir que la página se cargue siempre, sin importar si hay parámetros
    fetchTransactionStatus()
    
    // Configurar polling automático cada 10 segundos para transacciones pendientes
    const interval = setInterval(() => {
      if (transaction?.status === 'pending' || transaction?.status === 'PENDING') {
        fetchTransactionStatus()
      }
    }, 10000) // 10 segundos
    
    // Limpiar interval al desmontar o cuando la transacción ya no esté pendiente
    return () => clearInterval(interval)
  }, [transactionId, reference, transaction?.status])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
      case 'APPROVED':
      case 'confirmed':
        return <CheckCircle className="w-8 h-8 text-green-400" />
      case 'pending':
      case 'PENDING':
        return <Clock className="w-8 h-8 text-yellow-400" />
      case 'failed':
      case 'rejected':
      case 'FAILED':
      case 'CANCELLED':
        return <AlertCircle className="w-8 h-8 text-red-400" />
      default:
        return <Clock className="w-8 h-8 text-gray-400" />
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
      case 'APPROVED':
      case 'confirmed':
        return 'Transacción Completada'
      case 'pending':
      case 'PENDING':
        return 'Transacción Pendiente'
      case 'failed':
      case 'rejected':
      case 'FAILED':
        return 'Transacción Fallida'
      case 'CANCELLED':
        return 'Transacción Cancelada'
      default:
        return 'Estado Desconocido'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
      case 'APPROVED':
      case 'confirmed':
        return 'text-green-400'
      case 'pending':
      case 'PENDING':
        return 'text-yellow-400'
      case 'failed':
      case 'rejected':
      case 'FAILED':
      case 'CANCELLED':
        return 'text-red-400'
      default:
        return 'text-gray-400'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white text-lg">Cargando información de la transacción...</p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Error</h2>
            <p className="text-gray-300 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Volver al Inicio
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Estado de la Transacción */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 mb-8"
          >
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                {getStatusIcon(transaction?.status)}
              </div>
              <h1 className={`text-3xl font-bold mb-2 ${getStatusColor(transaction?.status)}`}>
                {getStatusText(transaction?.status)}
              </h1>
              <p className="text-gray-300">
                Referencia: <span className="font-mono text-white">{transaction?.reference || reference || 'N/A'}</span>
              </p>
              {transactionId && (
                <p className="text-gray-300 text-sm">
                  ID Wompi: <span className="font-mono text-white">{transactionId}</span>
                </p>
              )}
            </div>



            {/* Información de la Transacción */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Detalles de la Membresía */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Detalles de la Membresía
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Membresía:</span>
                    <span className="text-white font-medium">{transaction?.sorteo_name || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Cantidad de Boletos:</span>
                    <span className="text-white font-medium">{transaction?.quantity || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Monto Total:</span>
                    <span className="text-white font-medium">
                      ${transaction?.totalAmount?.toLocaleString() || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Fecha:</span>
                    <span className="text-white font-medium">
                      {transaction?.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Datos del Cliente */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Datos del Cliente
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-white">{transaction?.customer_data?.name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-white">{transaction?.customer_data?.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-white">{transaction?.customer_data?.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-white">
                      {transaction?.customer_data?.typedoc || 'N/A'}: {transaction?.customer_data?.document || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-white">{transaction?.customer_data?.city || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Widget de WOMPI - Solo mostrar si la transacción está pendiente */}
            {transaction?.status === 'pending' && (
              <div className="mt-8 mb-8">
                <h3 className="text-xl font-semibold text-white mb-4 text-center">Completar Pago</h3>
                <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <div className="text-center">
                    <p className="text-gray-300 mb-4">Completa tu pago de forma segura con WOMPI</p>
                    <div id="wompi-widget-container">
                      <WompiWidget 
                        reference={transaction?.reference || reference}
                        amount={transaction?.totalAmount || 0}
                        signature={generateWompiSignature(transaction?.reference || reference, transaction?.totalAmount || 0)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Números de Boletos */}
            {transaction?.tickets && transaction.tickets.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xl font-semibold text-white mb-4">Números de Boletos</h3>
                <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                  {transaction.tickets.map((ticket, index) => {
                    const isPending = transaction?.status === 'pending' || transaction?.status === 'PENDING'
                    const isCompleted = transaction?.status === 'completed' || transaction?.status === 'approved' || transaction?.status === 'confirmed'
                    
                    return (
                      <div
                        key={index}
                        className={`${
                          isPending 
                            ? 'bg-gray-500/20 border border-gray-400/50' 
                            : isCompleted
                            ? 'bg-green-600/20 border border-green-500/30'
                            : 'bg-primary-600/20 border border-primary-500/30'
                        } rounded-lg p-2 text-center`}
                      >
                        <span className={`font-mono text-sm ${
                          isPending 
                            ? 'text-gray-300' 
                            : isCompleted
                            ? 'text-green-300'
                            : 'text-white'
                        }`}>
                          {isPending ? '?' : String(ticket.numero).padStart(5, '0')}
                        </span>
                      </div>
                    )
                  })}
                </div>
                {transaction?.status === 'pending' && (
                  <p className="text-yellow-300 text-sm mt-3 text-center">
                    ⏳ Tus boletos están reservados. Los números se revelarán cuando se confirme el pago.
                  </p>
                )}
                {(transaction?.status === 'completed' || transaction?.status === 'approved' || transaction?.status === 'confirmed') && (
                  <p className="text-green-300 text-sm mt-3 text-center">
                    ✅ ¡Pago confirmado! Estos son tus números de boletos ganadores.
                  </p>
                )}
              </div>
            )}

            {/* Mensaje de Estado */}
            <div className="mt-8 p-4 rounded-lg bg-white/5 border border-white/10">
              {transaction?.status === 'pending' && (
                <div className="text-center">
                  <p className="text-yellow-300 mb-2">
                    Tu transacción está siendo procesada. Te notificaremos cuando se complete.
                  </p>
                  <p className="text-gray-400 text-sm mb-3">
                    Puedes cerrar esta página y volver más tarde para verificar el estado.
                  </p>
                  <p className="text-yellow-200 text-sm font-medium">
                    🎫 Tus números de boletos se mostrarán una vez que se confirme el pago.
                  </p>
                </div>
              )}
              {(transaction?.status === 'completed' || transaction?.status === 'approved' || transaction?.status === 'confirmed') && (
                <div className="text-center">
                  <p className="text-green-300 mb-2">
                    ¡Felicitaciones! Tu compra se ha completado exitosamente.
                  </p>
                  <p className="text-gray-400 text-sm">
                    Tus boletos han sido asignados y puedes verlos en tu cuenta.
                  </p>
                </div>
              )}
              {(transaction?.status === 'failed' || transaction?.status === 'rejected') && (
                <div className="text-center">
                  <p className="text-red-300 mb-2">
                    Lo sentimos, tu transacción no pudo ser procesada.
                  </p>
                  <p className="text-gray-400 text-sm">
                    Por favor, intenta nuevamente o contacta a soporte.
                  </p>
                </div>
              )}
            </div>

            {/* Botones de Acción */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
              <button
                onClick={() => navigate('/')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Volver al Inicio
              </button>
              <button
                onClick={() => fetchTransactionStatus(true)}
                disabled={refreshing}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg transition-colors flex items-center gap-2"
              >
                {refreshing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Actualizando...
                  </>
                ) : (
                  'Actualizar Estado'
                )}
              </button>
              <button
                onClick={() => navigate('/mi-cuenta')}
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                Ver Mi Cuenta
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
      
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          }
        }}
      />
    </div>
  )
}

// Componente para el Widget de WOMPI
const WompiWidget = ({ reference, amount, signature }) => {
  useEffect(() => {
    // Limpiar cualquier script previo
    const existingScript = document.getElementById('wompi-checkout-script')
    if (existingScript) {
      existingScript.remove()
    }

    // Crear el script de WOMPI
    const script = document.createElement('script')
    script.id = 'wompi-checkout-script'
    script.src = 'https://checkout.wompi.co/widget.js'
    script.setAttribute('data-render', 'button')
    script.setAttribute('data-public-key', 'pub_prod_z7tJ22gUkIjUBhZXqBKGfWoRDTcvuKWb')
    script.setAttribute('data-currency', 'COP')
    script.setAttribute('data-amount-in-cents', (amount * 100).toString())
    script.setAttribute('data-reference', reference)
    script.setAttribute('data-signature:integrity', signature)
    script.setAttribute('data-redirect-url', `https://vip.paraisotour.co/resultado-transaccion?ref=${reference}`)

    // Agregar el script al contenedor
    const container = document.getElementById('wompi-widget-container')
    if (container) {
      const form = document.createElement('form')
      form.appendChild(script)
      container.appendChild(form)
    }

    // Cleanup al desmontar
    return () => {
      const scriptToRemove = document.getElementById('wompi-checkout-script')
      if (scriptToRemove) {
        scriptToRemove.remove()
      }
    }
  }, [reference, amount, signature])

  return null
}

export default ResultadoTransaccion
